import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AnyObject, ErrorMessage, FieldOption, QueryParamsResource, ValueType } from '@mri/types';
import { delay, filter, finalize, map, mergeMap, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { isEqualArrayString } from '@mri/functions';

const waitPress = 100;
@Component({
  selector: 'app-field-select-multiple',
  styleUrl: './field-select-multiple.component.scss',
  templateUrl: './field-select-multiple.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldSelectMultipleComponent implements OnInit, OnDestroy {
  private _optionConfigKey$ = new Subject<string>();
  private _findInvert$ = new Subject<string[]>();
  private _search$ = new Subject<string>();
  private _subscription = new Subscription();
  private _classSet = new Set<string>();
  private _lastPressTime = new Date();
  private _options: FieldOption[] = [];
  private _optionConfigKey?: string;
  private _lastSearch = '';

  optionSelected = new Set<FieldOption>();
  valueSelected = new Set<string>();
  optionList: FieldOption[];
  isLoading: boolean;
  search: string;

  @Input() errors?: Array<ErrorMessage>;
  @Input() label: string;
  @Input() sizeResults: number = 20;
  @Input() optionsUrl?: string;
  @Input() optionsUrlInvert?: string;
  @Input() invertUrl?: string;
  @Input() placeholder?: string;
  @Input() filter?: Record<string, string | string[]>;

  @Input() set optionConfigKey(value: string) {
    if (!value) return;

    this._optionConfigKey = value;
    this._optionConfigKey$.next(value);
  }

  @Input() set options(options: FieldOption[]) {
    this._options = options;
    this._cdr.markForCheck();
  }

  @Input() set value(value: AnyObject) {
    if (!Array.isArray(value)) return;

    if (isEqualArrayString([...this.valueSelected], value)) return;

    this.valueSelected = new Set(value);

    if (value.length > 0) {
      this._findInvert$.next(value);
    } else {
      this.optionSelected.clear();
      this._cdr.markForCheck();
    }
  }

  @Output() valueChange = new EventEmitter<Array<string>>();

  get value(): string[] {
    return [...this.valueSelected];
  }

  get options(): FieldOption[] {
    return this._options || [];
  }

  constructor(
    private _genericResource: GenericResourceService,
    private _configResource: ConfigResourceService,
    private _cdr: ChangeDetectorRef
  ) {
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  ngOnInit(): void {
    this._subscription.add(
      this._search$
        .pipe(
          delay(waitPress),
          filter(() => this._checkTimePress()),
          mergeMap(search => this._consultOptions(search))
        )
        .subscribe()
    );

    this._subscription.add(
      this._findInvert$
        .pipe(
          filter(values => Array.isArray(values) && values.length > 0),
          mergeMap(search => this._findOptionFromUrl(search)),
          map(options => this._getSelectedOptions(options)),
          tap(options => this.optionSelected = new Set(options)),
          tap(options => {
            this.valueSelected = new Set(options.map(o => o.value));
            this._cdr.markForCheck();
          })
        ).subscribe()
    );

    this._subscription.add(
      this._optionConfigKey$
        .pipe(
          mergeMap(fieldKey => this._configResource.getOptions$(fieldKey)),
          filter(option => !!option),
          tap(option => {
            this._options = [...(this._options || []), ...option];
            this._cdr.markForCheck();
          }),
          tap(() => this.valueSelected && this._setOptionValue([...this.valueSelected]))
        ).subscribe()
    );

    if (this.valueSelected) this._setOptionValue([...this.valueSelected]);

    if (this._optionConfigKey) this._optionConfigKey$.next(this._optionConfigKey);
  }

  onChange(event: AnyObject) {
    const value = event.target.value.toLocaleString();

    const option = this.optionList.find(o => o.label.toLowerCase() === value);

    if (option) {
      this.onSelect(option);
    }
  }

  onSelect(option: FieldOption) {
    this.valueSelected.add(option.value);

    this.optionSelected.add(option);
    this._emitValue();
    setTimeout(() => {
      this.search = '';
      this._cdr.markForCheck();
    }, 10);
  }

  onRemove(option: FieldOption) {
    this.valueSelected.delete(option.value);

    this.optionSelected.delete(option);
    this._emitValue();
    this._cdr.markForCheck();
  }

  onKeyUp(event: AnyObject) {
    const search = (event?.target?.value || '').trim();
    const searchPrev = this._lastSearch.trim();

    if (searchPrev?.trim() === search) return;

    this._search$.next(search);
    this._lastSearch = search;
    this._lastPressTime = new Date();
  }

  onFocus() {
    this._classSet.add('card-focus');
    this._search$.next('');
    this._cdr.markForCheck();
  }

  onBlur() {
    this._classSet.delete('card-focus');
    this._cdr.markForCheck();
  }

  private _consultOptions(search: string = ''): Observable<AnyObject> {

    const optionsUrl = this.optionsUrl;
    if (!optionsUrl?.trim()) {
      this._showOptions(search);
      return of({});
    }

    this.isLoading = true;
    this._cdr.markForCheck();
    return this._genericResource
      .getElements<FieldOption>(optionsUrl, {
        filter: { search, ...(this.filter || {}) },
        paginate: { page: 1, size: this.sizeResults }
      })
      .pipe(
        catchError(() => {
          return of({ objects: [] });
        }),
        tap(response =>
          this._showOptions(search.toString(), response?.objects as Array<FieldOption>)
        ),
        finalize(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
        })
      );
  }

  private _showOptions(searchValue: string, asyncOptions?: FieldOption[]) {
    const { options } = this;
    const regex = new RegExp(searchValue.toString(), 'gi');
    const staticOptions = options?.filter(o => regex.test(o?.label));
    this.optionList = [...(staticOptions || []), ...(asyncOptions || [])];
    this._cdr.markForCheck();
  }

  private _emitValue() {
    this.valueChange.emit([...this.valueSelected]);
  }

  private _checkTimePress(): boolean {
    const { _lastPressTime } = this;
    if (!_lastPressTime) return false;

    return new Date().getTime() - _lastPressTime.getTime() > waitPress;
  }

  private _setOptionValue(value: string[]) {

    return this._findInvert$.next(value);
  }

  private _findOptionFromUrl(value: string[]): Observable<Array<FieldOption>> {
    if (!this.optionsUrl) return of([]);

    const filter = { value, ...(this.filter || {}) } as unknown as Record<string, ValueType | ValueType[]>;
    return this._genericResource.getElements<FieldOption>(this.optionsUrl, {
      filter,
      paginate: { page: 1, size: 100 }
    } as QueryParamsResource).pipe(
      map(response => response?.objects)
    );
  }

  private _getSelectedOptions(options: FieldOption[] = [] ): FieldOption[] {
    return [...this.valueSelected].map(value => {
      const option = [...this.options, ...options].find(o => o.value === value);

      if (option) return option;

      return { label: `(${value})`, value };
    });
  }
}
